import { create } from 'zustand';
import {
  readJurisdictionFromEnvironment,
  resolveRegulatoryPolicy,
} from './resolvePolicy';
import type { RegulatoryPolicy } from './types';
import { getGameSessionId } from '@core/utils/gameSession';
import { getActiveGameStoreOrNull } from '@core/gameengine/state/activeGameStore';
import { isSkyxNewGameReady } from '@core/utils/skyxNewBootstrap';
import { getGameFromUrl } from '@core/utils/urlParser';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';

export interface GamingSessionState {
  policy: RegulatoryPolicy;
  gameSessionId: string | null;
  /** Wall-clock ms when this gaming session started (when game UI is shown). */
  sessionStartedAtMs: number | null;
  /** Balance snapshot at session start (account currency units). */
  sessionStartBalance: number | null;
  /**
   * Last wallet balance observed while this UKGC session is active.
   * Updated on every server balance change; used on reconnect reconcile.
   */
  lastKnownBalance: number | null;
  /**
   * Reconnect net delta computed while a round is still in progress.
   * Applied to sessionNetPosition only when the round settles (FLEW_AWAY).
   */
  pendingReconnectNetDelta: number | null;
  /** Cumulative net win/loss since session start (updated on round settle). */
  sessionNetPosition: number;
  /** Pending stake per side for the active round (not yet settled). */
  pendingRoundStakes: { A: number | null; B: number | null };
  /** Payout recorded on cashout before round end (per side). */
  pendingRoundPayouts: { A: number | null; B: number | null };
  /**
   * Sides settled as a loss at FLEW_AWAY (stake amount). Used to correct
   * session net if a late cashout ACK arrives after settle.
   */
  settledAsLossStakes: { A: number | null; B: number | null };

  setPolicyFromJurisdiction: (jurisdiction?: string | null) => void;

  startGamingSession: (input: {
    gameSessionId: string;
    startingBalance: number;
    reason?: string;
  }) => void;
  clearGamingSession: (reason?: string) => void;
  noteBetPlaced: (side: 'A' | 'B', stake: number) => void;
  noteBetCashout: (side: 'A' | 'B', stake: number, payout: number) => void;
  settleRoundNetPosition: () => void;
  ensureSessionStartBalance: (balance: number) => void;
  /** Keep lastKnownBalance in sync on every wallet update while session is active. */
  trackSessionBalance: (balance: number) => void;
}

export const useGamingSessionStore = create<GamingSessionState>((set, get) => ({
  policy: resolveRegulatoryPolicy(readJurisdictionFromEnvironment()),
  gameSessionId: null,
  sessionStartedAtMs: null,
  sessionStartBalance: null,
  lastKnownBalance: null,
  pendingReconnectNetDelta: null,
  sessionNetPosition: 0,
  pendingRoundStakes: { A: null, B: null },
  pendingRoundPayouts: { A: null, B: null },
  settledAsLossStakes: { A: null, B: null },

  setPolicyFromJurisdiction: jurisdiction => {
    set({ policy: resolveRegulatoryPolicy(jurisdiction) });
  },

  startGamingSession: ({ gameSessionId, startingBalance, reason }) => {
    const startedAtMs = Date.now();
    console.log('[UKGC] Session timer STARTED (fresh)', {
      reason: reason ?? 'startGamingSession',
      gameSessionId,
      startedAtMs,
      startedAtIso: new Date(startedAtMs).toISOString(),
      startingBalance,
    });
    set({
      gameSessionId,
      sessionStartedAtMs: startedAtMs,
      sessionStartBalance: startingBalance,
      lastKnownBalance: startingBalance,
      pendingReconnectNetDelta: null,
      sessionNetPosition: 0,
      pendingRoundStakes: { A: null, B: null },
      pendingRoundPayouts: { A: null, B: null },
      settledAsLossStakes: { A: null, B: null },
    });
  },

  clearGamingSession: reason => {
    const id = get().gameSessionId;
    const startedAtMs = get().sessionStartedAtMs;
    if (id != null || startedAtMs != null) {
      console.log('[UKGC] Session timer CLEARED', {
        reason: reason ?? 'clearGamingSession',
        gameSessionId: id,
        startedAtMs,
        startedAtIso:
          startedAtMs != null ? new Date(startedAtMs).toISOString() : null,
      });
    }
    set({
      gameSessionId: null,
      sessionStartedAtMs: null,
      sessionStartBalance: null,
      lastKnownBalance: null,
      pendingReconnectNetDelta: null,
      sessionNetPosition: 0,
      pendingRoundStakes: { A: null, B: null },
      pendingRoundPayouts: { A: null, B: null },
      settledAsLossStakes: { A: null, B: null },
    });
  },

  noteBetPlaced: (side, stake) => {
    set(state => ({
      pendingRoundStakes: { ...state.pendingRoundStakes, [side]: stake },
      settledAsLossStakes: { ...state.settledAsLossStakes, [side]: null },
    }));
  },

  noteBetCashout: (side, stake, payout) => {
    const state = get();

    if (state.pendingReconnectNetDelta != null) {
      if (typeof state.pendingRoundPayouts[side] === 'number') {
        return;
      }
      const nextDeferred = state.pendingReconnectNetDelta + payout;
      console.log('[UKGC] Cashout folded into deferred reconnect net', {
        side,
        stake,
        payout,
        previousDeferred: state.pendingReconnectNetDelta,
        nextDeferred,
      });
      set({
        pendingReconnectNetDelta: nextDeferred,
        pendingRoundPayouts: {
          ...state.pendingRoundPayouts,
          [side]: payout,
        },
      });
      return;
    }
    const lossStake = state.settledAsLossStakes[side];
    if (lossStake != null && lossStake > 0) {
      const nextNet = state.sessionNetPosition + payout;
      set({
        sessionNetPosition: nextNet,
        settledAsLossStakes: { ...state.settledAsLossStakes, [side]: null },
      });
      return;
    }
    set(s => ({
      pendingRoundStakes: {
        ...s.pendingRoundStakes,
        [side]: s.pendingRoundStakes[side] ?? stake,
      },
      pendingRoundPayouts: { ...s.pendingRoundPayouts, [side]: payout },
    }));
  },

  settleRoundNetPosition: () => {
    const {
      pendingRoundStakes,
      pendingRoundPayouts,
      sessionNetPosition,
      pendingReconnectNetDelta,
    } = get();

    // Reconnect while round was in progress: apply deferred delta at round end.
    if (pendingReconnectNetDelta != null) {
      const nextNet = sessionNetPosition + pendingReconnectNetDelta;
      console.log('[UKGC] Applying deferred reconnect net at round end', {
        previousNet: sessionNetPosition,
        pendingReconnectNetDelta,
        nextNet,
      });
      set({
        sessionNetPosition: nextNet,
        pendingReconnectNetDelta: null,
        pendingRoundStakes: { A: null, B: null },
        pendingRoundPayouts: { A: null, B: null },
        settledAsLossStakes: { A: null, B: null },
      });
      return;
    }

    let roundDelta = 0;
    let hadActivity = false;
    const settledAsLossStakes: { A: number | null; B: number | null } = {
      A: null,
      B: null,
    };
    (['A', 'B'] as const).forEach(side => {
      const stake = pendingRoundStakes[side];
      if (stake == null || stake <= 0) return;
      hadActivity = true;
      const payout = pendingRoundPayouts[side];
      if (typeof payout === 'number') {
        roundDelta += payout - stake;
      } else {
        roundDelta -= stake;
        settledAsLossStakes[side] = stake;
      }
    });
    if (!hadActivity) {
      set({
        pendingRoundStakes: { A: null, B: null },
        pendingRoundPayouts: { A: null, B: null },
        settledAsLossStakes: { A: null, B: null },
      });
      return;
    }
    set({
      sessionNetPosition: sessionNetPosition + roundDelta,
      pendingRoundStakes: { A: null, B: null },
      pendingRoundPayouts: { A: null, B: null },
      settledAsLossStakes,
    });
  },

  ensureSessionStartBalance: balance => {
    const state = get();
    if (!state.gameSessionId || state.sessionStartedAtMs == null) return;
    if (state.sessionStartBalance != null && state.sessionStartBalance !== 0) {
      return;
    }
    if (state.sessionNetPosition !== 0) return;
    if (!(balance > 0)) return;
    set({
      sessionStartBalance: balance,
      lastKnownBalance: state.lastKnownBalance ?? balance,
    });
  },

  trackSessionBalance: balance => {
    const state = get();
    if (!state.policy.sessionDisplays) return;
    if (state.sessionStartedAtMs == null || state.gameSessionId == null) return;
    if (!Number.isFinite(balance)) return;
    if (state.lastKnownBalance === balance) return;
    set({ lastKnownBalance: balance });
  },
}));

export function getRegulatoryPolicy(): RegulatoryPolicy {
  return useGamingSessionStore.getState().policy;
}

export function initRegulatoryPolicy(
  jurisdiction?: string | null
): RegulatoryPolicy {
  const policy = resolveRegulatoryPolicy(
    jurisdiction ?? readJurisdictionFromEnvironment()
  );
  useGamingSessionStore.setState({ policy });
  return policy;
}

export function tryStartFreshGamingSession(reason: string): void {
  const store = useGamingSessionStore.getState();
  if (!store.policy.sessionDisplays) return;

  const game = getGameFromUrl();
  if (isSkyxNewRoute(game) && !isSkyxNewGameReady()) {
    console.log('[UKGC] Session start deferred — game UI not ready yet', {
      reason,
    });
    return;
  }

  const connectionStatus = getActiveGameStoreOrNull()?.getState()
    ?.connectionStatus as string | undefined;
  if (connectionStatus && connectionStatus !== 'connected') {
    console.log('[UKGC] Session start deferred — not connected', {
      reason,
      connectionStatus,
    });
    return;
  }

  const sessionId = getGameSessionId();
  if (!sessionId) {
    console.warn('[UKGC] Session start skipped — no gameSessionId', { reason });
    return;
  }

  // Same backend session → keep wall-clock start (reconnect is not a new session).
  if (
    store.sessionStartedAtMs != null &&
    store.gameSessionId != null &&
    store.gameSessionId === sessionId
  ) {
    console.log('[UKGC] Session timer preserved (same gameSessionId)', {
      reason,
      gameSessionId: sessionId,
      sessionStartedAtMs: store.sessionStartedAtMs,
    });
    return;
  }

  const balance = getActiveGameStoreOrNull()?.getState()?.balance ?? 0;
  store.startGamingSession({
    gameSessionId: sessionId,
    startingBalance: typeof balance === 'number' ? balance : 0,
    reason,
  });
}

export function clearGamingSessionOnHide(reason: string): void {
  const store = useGamingSessionStore.getState();
  if (store.policy.sessionDisplays) {
    console.log('[UKGC] Skipping session clear on socket lifecycle', {
      reason,
      gameSessionId: store.gameSessionId,
      sessionStartedAtMs: store.sessionStartedAtMs,
    });
    return;
  }
  store.clearGamingSession(reason);
}

/** Mirror wallet balance into the UKGC session while it is active. */
export function trackSessionBalance(balance: number): void {
  useGamingSessionStore.getState().trackSessionBalance(balance);
}

function sumPendingRoundStakes(stakes: {
  A: number | null;
  B: number | null;
}): number {
  let total = 0;
  (['A', 'B'] as const).forEach(side => {
    const stake = stakes[side];
    if (stake != null && stake > 0) total += stake;
  });
  return total;
}

/**
 * After reconnect/JOIN while continuing the same UKGC session:
 *   delta = -pendingBets + (newServerBalance - currentBalance)
 * If the round is still in progress, stash delta and apply it only when the
 * round settles (FLEW_AWAY). Otherwise apply immediately.
 * Skipped when the session is inactive or resetting (new gameSessionId).
 */
export function reconcileReconnectBalance(
  newBalance: number,
  options?: { roundInProgress?: boolean }
): void {
  const store = useGamingSessionStore.getState();
  if (!store.policy.sessionDisplays) return;
  if (store.sessionStartedAtMs == null || store.gameSessionId == null) return;

  const launchId = getGameSessionId();
  if (!launchId || launchId !== store.gameSessionId) {
    // New launch id → tryStartFreshGamingSession will reset; do not adjust net.
    return;
  }
  if (!Number.isFinite(newBalance)) return;

  const currentBalance = store.lastKnownBalance;
  if (currentBalance == null || !Number.isFinite(currentBalance)) {
    store.trackSessionBalance(newBalance);
    return;
  }

  const pendingBets = sumPendingRoundStakes(store.pendingRoundStakes);
  const balanceDiff = newBalance - currentBalance;
  const delta = -pendingBets + balanceDiff;

  if (Math.abs(delta) < 1e-9) {
    store.trackSessionBalance(newBalance);
    return;
  }

  const roundInProgress = options?.roundInProgress === true;

  if (roundInProgress) {
    const deferred = (store.pendingReconnectNetDelta ?? 0) + delta;
    console.log('[UKGC] Reconnect net deferred until round end', {
      previousNet: store.sessionNetPosition,
      pendingBets,
      currentBalance,
      newServerBalance: newBalance,
      balanceDiff,
      delta,
      deferred,
      gameSessionId: store.gameSessionId,
    });
    useGamingSessionStore.setState({
      lastKnownBalance: newBalance,
      pendingReconnectNetDelta: deferred,
      // Folded into deferred — do not settle again on FLEW_AWAY via pending.
      pendingRoundStakes: { A: null, B: null },
      pendingRoundPayouts: { A: null, B: null },
      settledAsLossStakes: { A: null, B: null },
    });
    return;
  }

  const nextNet = store.sessionNetPosition + delta;
  console.log('[UKGC] Reconnect net reconcile (between rounds)', {
    previousNet: store.sessionNetPosition,
    pendingBets,
    currentBalance,
    newServerBalance: newBalance,
    balanceDiff,
    delta,
    nextNet,
    gameSessionId: store.gameSessionId,
  });

  useGamingSessionStore.setState({
    sessionNetPosition: nextNet,
    lastKnownBalance: newBalance,
    pendingReconnectNetDelta: null,
    pendingRoundStakes: { A: null, B: null },
    pendingRoundPayouts: { A: null, B: null },
    settledAsLossStakes: { A: null, B: null },
  });
}
